import { getBranding, saveBranding, ApiError } from '@/services/api';
import { getStoredToken } from '@/services/auth';
import { textOn } from '@/ui/molecules/brand-badge/brand-badge';
import type { CreatorConfig, CreatorSecondaryLink } from '@/types';

const MAX_SECONDARY_LINKS = 10;
const DEFAULT_ACCENT = '#FF0033';

// ─── DOM refs ───
const panel          = document.getElementById('branding-panel')!;
const badge          = document.getElementById('branding-badge')!;
const lockedNotice   = document.getElementById('branding-locked')!;
const editor         = document.getElementById('branding-editor')!;
const errorEl        = document.getElementById('branding-error')!;

const brandTitle     = document.getElementById('brand-title') as HTMLInputElement;
const brandDesc      = document.getElementById('brand-description') as HTMLTextAreaElement;
const colorPicker    = document.getElementById('brand-color') as HTMLInputElement;
const colorHex       = document.getElementById('brand-color-hex') as HTMLInputElement;
const textColorPicker = document.getElementById('brand-text-color') as HTMLInputElement;
const textColorHex   = document.getElementById('brand-text-color-hex') as HTMLInputElement;
const logoUrl        = document.getElementById('brand-logo') as HTMLInputElement;
const backgroundUrl  = document.getElementById('brand-background') as HTMLInputElement;
const promoBackgroundUrl = document.getElementById('brand-promo-background') as HTMLInputElement;

const ctaTitle       = document.getElementById('cta-title') as HTMLInputElement;
const ctaSubtitle    = document.getElementById('cta-subtitle') as HTMLTextAreaElement;
const ctaButton      = document.getElementById('cta-button') as HTMLInputElement;
const ctaUrl         = document.getElementById('cta-url') as HTMLInputElement;

const showInPlayer   = document.getElementById('show-in-player') as HTMLInputElement;
const showSidebar    = document.getElementById('show-sidebar') as HTMLInputElement;
const showInPopup    = document.getElementById('show-in-popup') as HTMLInputElement;
const placementsNote = document.getElementById('placements-note')!;

const linksContainer = document.getElementById('branding-links')!;
const linksEmpty     = document.getElementById('links-empty')!;
const linksCount     = document.getElementById('links-count')!;
const addLinkBtn     = document.getElementById('add-link-btn') as HTMLButtonElement;

const saveBtn        = document.getElementById('branding-save-btn') as HTMLButtonElement;
const resetBtn       = document.getElementById('branding-reset-btn') as HTMLButtonElement;
const unsavedNote    = document.getElementById('branding-unsaved')!;

const previewRoot      = document.getElementById('branding-preview')!;
const previewOff       = document.getElementById('preview-off')!;
const previewNoCta     = document.getElementById('preview-no-cta')!;

// In the player — the band the extension lays over the video
const previewPlayerCap = document.getElementById('preview-caption-player')!;
const previewPlayer    = document.getElementById('preview-player')!;
const previewMark      = document.getElementById('preview-mark')!;
const previewBadgeTitle  = document.getElementById('preview-badge-title')!;
const previewBadgeSub    = document.getElementById('preview-badge-subtitle')!;
const previewBadgeButton = document.getElementById('preview-badge-button')!;

// In the sidebar — artwork, the offer, the secondary links
const previewSidebarCap = document.getElementById('preview-caption-sidebar')!;
const previewSidebar   = document.getElementById('preview-sidebar')!;
const previewArtwork   = document.getElementById('preview-artwork')!;
const previewCtaTitle  = document.getElementById('preview-cta-title')!;
const previewCtaSub    = document.getElementById('preview-cta-subtitle')!;
const previewCtaButton = document.getElementById('preview-cta-button')!;
const previewLinks     = document.getElementById('preview-links')!;

// In the extension popup — the brand title and description live here
const previewLogo      = document.getElementById('preview-logo')!;
const previewName      = document.getElementById('preview-name')!;
const previewIntro     = document.getElementById('preview-intro')!;
const previewIntroTitle = document.getElementById('preview-intro-title')!;
const previewDesc      = document.getElementById('preview-description')!;
const previewPopupOffer  = document.getElementById('preview-popup-offer')!;
const previewPopupTitle  = document.getElementById('preview-popup-title')!;
const previewPopupSub    = document.getElementById('preview-popup-subtitle')!;
const previewPopupButton = document.getElementById('preview-popup-button')!;

// ─── State ───
let savedConfig: CreatorConfig | null = null;
let channelName = '';
/** The form as last loaded or saved, to tell the creator what is unsaved. */
let savedSnapshot = '';
/** The pending "Saved" label reset, so a later save can cancel it. */
let savedLabelTimer = 0;
/** Gives each link row's boxes ids of their own, for their labels. */
let linkRowSeq = 0;

// ─── Helpers ───

const HEX = /^#[0-9a-fA-F]{6}$/;
/** What the contrast rule answers for a dark accent — white, as a token. */
const AUTO_LIGHT_TEXT = textOn('#000000');

/**
 * The automatic text colour as a hex, for the swatch, which cannot show a
 * token. Read from the page so it cannot disagree with what the preview paints.
 */
function autoTextColorHex(): string {
  const token = onAccent().replace(/^var\(|\)$/g, '');
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return HEX.test(value) ? value : (onAccent() === AUTO_LIGHT_TEXT ? '#FFFFFF' : '#1D1D1F');
}

function accent(): string {
  const value = colorHex.value.trim().toUpperCase();
  return HEX.test(value) ? value : DEFAULT_ACCENT;
}

/**
 * What is written on the accent: the creator's choice, or — left empty — the
 * extension's own contrast rule, which is white on all but very pale colours.
 */
function onAccent(): string {
  const value = textColorHex.value.trim().toUpperCase();
  return HEX.test(value) ? value : textOn(accent());
}

/** Only http(s) is previewed — the extension will not follow anything else. */
function safeUrl(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Where a link goes, as the extension's card prints it: host and path, no
 * scheme. The host alone repeats the offer's own domain on every row, since a
 * creator's links usually sit on their own site. Mirrors utils/creator.ts's
 * linkTarget in the extension.
 */
function linkTarget(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '') + parsed.pathname.replace(/\/$/, '');
  } catch {
    return url;
  }
}

function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'FC';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function setError(message: string | null): void {
  errorEl.textContent = message ?? '';
  errorEl.classList.toggle('show', !!message);
}

/** `null` for a blank field — the backend treats absent and blank identically. */
function value(input: HTMLInputElement | HTMLTextAreaElement): string | null {
  const trimmed = input.value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// ─── Secondary link rows ───

function linkRows(): HTMLElement[] {
  return Array.from(linksContainer.querySelectorAll<HTMLElement>('.fc-link-row'));
}

function refreshLinkChrome(): void {
  const count = linkRows().length;
  linksEmpty.style.display = count === 0 ? '' : 'none';

  // The cap is real — the backend rejects an eleventh link outright
  // (CreatorBrandingService.MAX_SECONDARY_LINKS) — so it is stated alongside
  // the other limits, and on the button once it applies.
  const atCap = count >= MAX_SECONDARY_LINKS;
  addLinkBtn.disabled = atCap;
  addLinkBtn.textContent = atCap ? `Maximum of ${MAX_SECONDARY_LINKS} links` : 'Add a link';

  // Counted in the card's head, where the character counters sit on a field.
  linksCount.textContent = `${count} / ${MAX_SECONDARY_LINKS}`;
  linksCount.classList.toggle('fc-card__count--full', atCap);
}

/**
 * One editable link: title, where it goes and its button on one line, then an
 * optional description. Each box carries its own label and, where there is a
 * limit, its own counter — the same shape as the fields above, rather than a
 * strip of unlabelled boxes.
 */
function addLinkRow(link?: CreatorSecondaryLink): void {
  if (linkRows().length >= MAX_SECONDARY_LINKS) return;

  const row = document.createElement('div');
  row.className = 'fc-link-row';

  // Ids are per row, so each box can name its own label and counter. A shared
  // name would leave ten identical fields to a screen reader.
  const seq = ++linkRowSeq;
  const ids = {
    title: `link-${seq}-title`,
    url: `link-${seq}-url`,
    badge: `link-${seq}-badge`,
    subtitle: `link-${seq}-subtitle`,
  };

  row.innerHTML = `
    <div class="fc-link-row__bar">
      <button type="button" class="fc-link-row__remove" title="Remove this link" aria-label="Remove this link">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>
      </button>
    </div>

    <div class="fc-link-row__field">
      <div class="fc-link-row__head">
        <span class="fc-link-row__label" id="${ids.title}-label">Title</span>
        <span class="fc-link-row__count" data-count="title" id="${ids.title}-count"></span>
      </div>
      <input type="text" class="fc-link-row__input" data-link="title" maxlength="80"
             id="${ids.title}" aria-labelledby="${ids.title}-label"
             aria-describedby="${ids.title}-count"
             placeholder="Reset + Rebuild: the 6-week programme" />
    </div>

    <div class="fc-link-row__pair">
      <div class="fc-link-row__field fc-link-row__field--url">
        <div class="fc-link-row__head"><span class="fc-link-row__label" id="${ids.url}-label">Link</span></div>
        <input type="url" class="fc-link-row__input" data-link="url" spellcheck="false"
               id="${ids.url}" aria-labelledby="${ids.url}-label"
               placeholder="https://strongbyizzy.com/reset" />
      </div>
      <div class="fc-link-row__field fc-link-row__field--badge">
        <div class="fc-link-row__head">
          <span class="fc-link-row__label" id="${ids.badge}-label">Button</span>
          <span class="fc-link-row__count" data-count="badge" id="${ids.badge}-count"></span>
        </div>
        <input type="text" class="fc-link-row__input" data-link="badge" maxlength="24"
               id="${ids.badge}" aria-labelledby="${ids.badge}-label"
               aria-describedby="${ids.badge}-count" placeholder="Go" />
      </div>
    </div>

    <div class="fc-link-row__field">
      <div class="fc-link-row__head">
        <span class="fc-link-row__label" id="${ids.subtitle}-label">Description</span>
        <span class="fc-link-row__optional">optional</span>
        <span class="fc-link-row__count" data-count="subtitle" id="${ids.subtitle}-count"></span>
      </div>
      <textarea class="fc-link-row__input fc-link-row__input--prose" data-link="subtitle" rows="2" maxlength="140"
                id="${ids.subtitle}" aria-labelledby="${ids.subtitle}-label"
                aria-describedby="${ids.subtitle}-count"
                placeholder="One line on what it is and who it’s for."></textarea>
    </div>
  `;

  const field = (name: string) =>
    row.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-link="${name}"]`)!;

  field('title').value = link?.title ?? '';
  field('badge').value = link?.badge_text ?? '';
  field('url').value = link?.url ?? '';
  field('subtitle').value = link?.subtitle ?? '';

  // Each capped box counts itself, and says so in red once it is full.
  (['title', 'badge', 'subtitle'] as const).forEach((name) => {
    const input = field(name);
    const counter = row.querySelector<HTMLElement>(`[data-count="${name}"]`)!;
    const paint = () => {
      const length = input.value.length;
      const max = input.maxLength;
      counter.textContent = `${length} / ${max}`;
      counter.classList.toggle('fc-link-row__count--near', length >= max * 0.9 && length < max);
      counter.classList.toggle('fc-link-row__count--full', length >= max);
      input.classList.toggle('fc-link-row__input--full', isFull(input));
    };
    input.addEventListener('input', paint);
    paint();
  });

  const remove = row.querySelector<HTMLButtonElement>('.fc-link-row__remove')!;
  const nameRemove = () => {
    const title = field('title').value.trim();
    const label = title ? `Remove the link “${title}”` : 'Remove this link';
    remove.setAttribute('aria-label', label);
    remove.title = label;
  };
  field('title').addEventListener('input', nameRemove);
  nameRemove();

  remove.addEventListener('click', () => {
    row.remove();
    refreshLinkChrome();
    updatePreview();
  });

  row.addEventListener('input', updatePreview);

  linksContainer.appendChild(row);
  refreshLinkChrome();
}

function collectLinks(): CreatorSecondaryLink[] {
  return linkRows()
    .map((row) => {
      const field = (name: string) =>
        row.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-link="${name}"]`)!.value.trim();

      return {
        title: field('title') || null,
        subtitle: field('subtitle') || null,
        badge_text: field('badge') || null,
        url: field('url') || null,
      };
    })
    // A link needs both a label and a destination to be worth showing.
    .filter((link) => link.title && link.url);
}

// ─── Form <-> config ───

function collectConfig(): CreatorConfig {
  return {
    branding: {
      title: value(brandTitle),
      description: value(brandDesc),
      primary_color: value(colorHex),
      primary_text_color: value(textColorHex),
      logo_url: value(logoUrl),
      background_url: value(backgroundUrl),
      promo_background_url: value(promoBackgroundUrl),
    },
    monetization: {
      show_in_player_cta: showInPlayer.checked,
      show_sidebar: showSidebar.checked,
      show_in_popup: showInPopup.checked,
      primary_cta: {
        title: value(ctaTitle),
        subtitle: value(ctaSubtitle),
        button_text: value(ctaButton),
        url: value(ctaUrl),
      },
      secondary_links: collectLinks(),
    },
  };
}

function populateForm(config: CreatorConfig): void {
  const brand = config.branding ?? {};
  const cta = config.monetization?.primary_cta ?? {};

  brandTitle.value = brand.title ?? '';
  brandDesc.value = brand.description ?? '';
  colorHex.value = brand.primary_color ?? '';
  colorPicker.value = HEX.test(colorHex.value) ? colorHex.value : DEFAULT_ACCENT;
  textColorHex.value = brand.primary_text_color ?? '';
  logoUrl.value = brand.logo_url ?? '';
  backgroundUrl.value = brand.background_url ?? '';
  promoBackgroundUrl.value = brand.promo_background_url ?? '';

  ctaTitle.value = cta.title ?? '';
  ctaSubtitle.value = cta.subtitle ?? '';
  ctaButton.value = cta.button_text ?? '';
  ctaUrl.value = cta.url ?? '';

  // Absent means on — that was the behaviour before these flags existed.
  showInPlayer.checked = config.monetization?.show_in_player_cta !== false;
  showSidebar.checked = config.monetization?.show_sidebar !== false;
  showInPopup.checked = config.monetization?.show_in_popup !== false;

  linksContainer.innerHTML = '';
  (config.monetization?.secondary_links ?? []).forEach((link) => addLinkRow(link));
  refreshLinkChrome();

  savedSnapshot = JSON.stringify(collectConfig());
  paintCounters();
  updatePreview();
}

/** "Live" once the creator has given us something a viewer would actually see. */
function isConfigured(config: CreatorConfig | null): boolean {
  if (!config) return false;
  const brand = config.branding ?? {};
  const cta = config.monetization?.primary_cta ?? {};
  return !!(
    brand.title ||
    brand.description ||
    brand.primary_color ||
    brand.logo_url ||
    cta.title ||
    (config.monetization?.secondary_links ?? []).length > 0
  );
}

// ─── Character counters ───
//  Presentation only: a counter reads the field it names and writes nothing
//  back, so nothing here can affect what is saved. `maxlength` already stops
//  overtyping — the counter says how much room is left, and turns darker in
//  the last tenth and red at the limit, so a field that stopped taking input
//  explains itself.

const counterPainters: Array<() => void> = [];

function isFull(field: HTMLInputElement | HTMLTextAreaElement): boolean {
  return field.maxLength > 0 && field.value.length >= field.maxLength;
}

function initCounters(): void {
  document.querySelectorAll<HTMLElement>('[data-count-for]').forEach((counter) => {
    const id = counter.dataset.countFor!;
    const field = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!field || field.maxLength <= 0) return;

    const max = field.maxLength;
    const paint = () => {
      const length = field.value.length;
      counter.textContent = `${length} / ${max}`;
      counter.classList.toggle('fc-field__count--near', length >= max * 0.9 && length < max);
      counter.classList.toggle('fc-field__count--full', length >= max);
    };
    field.addEventListener('input', paint);
    counterPainters.push(paint);
    paint();
  });
}

/** Setting `.value` fires no input event, so a form filled in code repaints here. */
function paintCounters(): void {
  counterPainters.forEach((paint) => paint());
}

// ─── Live preview ───
//  Redrawn from the form on every edit. Nothing here is saved: the preview is
//  the unsaved form, and a reload shows the stored config again.

function paintMark(el: HTMLElement, logo: string | null, mono: string): void {
  el.innerHTML = '';
  if (!logo) { el.textContent = mono; return; }
  const img = document.createElement('img');
  img.src = logo;
  img.alt = '';
  // A URL a creator typed is as likely to 404 as to load; falling back to the
  // monogram keeps the preview honest instead of showing a broken glyph.
  img.onerror = () => { el.innerHTML = ''; el.textContent = mono; };
  el.appendChild(img);
}

function setText(el: HTMLElement, text: string): void {
  el.textContent = text;
  el.style.display = text ? '' : 'none';
}

function cssUrl(url: string | null): string {
  return url ? `url(${JSON.stringify(url)})` : 'none';
}

function updatePreview(): void {
  const color = accent();
  const textColor = onAccent();
  panel.style.setProperty('--brand-accent', color);
  colorPicker.value = color;
  // The picker cannot show "automatic", so it shows what automatic resolved to.
  textColorPicker.value = HEX.test(textColor) ? textColor : autoTextColorHex();
  previewRoot.style.setProperty('--fc-creator-accent', color);
  previewRoot.style.setProperty('--fc-creator-on-accent', textColor);

  // Artwork, with the extension's fallbacks: the promo surfaces use the promo
  // image or else the background; the popup banner uses only the background.
  const background = safeUrl(backgroundUrl.value);
  const promo = safeUrl(promoBackgroundUrl.value) ?? background;
  previewRoot.style.setProperty('--fc-preview-backdrop', cssUrl(background));
  previewRoot.style.setProperty('--fc-preview-artwork', cssUrl(promo));
  previewArtwork.style.display = promo ? '' : 'none';

  const logo = safeUrl(logoUrl.value);
  const title = brandTitle.value.trim();
  const shownName = title || channelName || 'Your channel';
  // As the extension does: the in-player mark is initialled from the brand
  // title, the popup's from the channel itself.
  paintMark(previewMark, logo, monogram(shownName));
  paintMark(previewLogo, logo, monogram(channelName || shownName));

  // The primary CTA — what both promo placements are built around.
  const ctaHeading = ctaTitle.value.trim();
  const subtitle = ctaSubtitle.value.trim();
  const buttonText = ctaButton.value.trim();
  const hasCta = !!(ctaHeading || subtitle || buttonText);
  // The extension only draws a button it can send the viewer somewhere with.
  const button = buttonText && safeUrl(ctaUrl.value) ? buttonText : '';

  setText(previewBadgeTitle, ctaHeading);
  setText(previewBadgeSub, subtitle);
  setText(previewBadgeButton, button);
  setText(previewCtaTitle, ctaHeading);
  setText(previewCtaSub, subtitle);
  setText(previewCtaButton, button);
  setText(previewPopupTitle, ctaHeading);
  setText(previewPopupSub, subtitle);
  setText(previewPopupButton, button);

  // Secondary links, as the sidebar card lists them.
  previewLinks.innerHTML = '';
  collectLinks().forEach((link) => {
    const url = safeUrl(link.url ?? '');
    if (!url) return;

    const row = document.createElement('div');
    row.className = 'fc-brand-preview__link';

    const text = document.createElement('span');
    text.className = 'fc-brand-preview__link-text';
    const label = document.createElement('span');
    label.className = 'fc-brand-preview__link-title';
    label.textContent = link.title ?? '';
    // As the extension does: the creator's own line, or where the link goes.
    const where = document.createElement('span');
    where.className = 'fc-brand-preview__link-host';
    where.textContent = (link.subtitle ?? '').trim() || linkTarget(url);
    text.append(label, where);
    row.appendChild(text);

    if (link.badge_text) {
      const badgeEl = document.createElement('span');
      badgeEl.className = 'fc-brand-preview__link-badge';
      badgeEl.textContent = link.badge_text;
      row.appendChild(badgeEl);
    }

    previewLinks.appendChild(row);
  });

  // Placements, gated the way the extension gates them: switched on, and
  // something to show.
  const inPlayerOn = showInPlayer.checked && hasCta;
  const sidebarOn = showSidebar.checked && hasCta;
  previewPlayerCap.style.display = inPlayerOn ? '' : 'none';
  previewPlayer.style.display = inPlayerOn ? '' : 'none';
  previewSidebarCap.style.display = sidebarOn ? '' : 'none';
  previewSidebar.style.display = sidebarOn ? '' : 'none';

  // The popup carries the offer too, and only the offer: the creator's lockup
  // and intro are shown there whatever this toggle says. It is also stricter
  // than the watch-page surfaces — it draws nothing without a title and a link
  // it can follow — so the preview has to be, or it promises a bar no viewer
  // will see.
  const popupOfferShown = showInPopup.checked && !!ctaHeading && !!safeUrl(ctaUrl.value);
  previewPopupOffer.style.display = popupOfferShown ? '' : 'none';

  const allOff = !showInPlayer.checked && !showSidebar.checked && !showInPopup.checked;
  previewOff.style.display = allOff ? '' : 'none';
  placementsNote.style.display = allOff ? '' : 'none';
  previewNoCta.style.display = !allOff && !hasCta ? '' : 'none';

  // The popup header: channel, then the brand title and description.
  previewName.textContent = channelName || shownName;
  setText(previewIntroTitle, title);
  setText(previewDesc, brandDesc.value.trim());
  previewIntro.style.display = title || brandDesc.value.trim() ? '' : 'none';

  refreshUnsaved();
}

/** Say so when the preview is showing edits a viewer cannot see yet. */
function refreshUnsaved(): void {
  const dirty = savedSnapshot !== '' && JSON.stringify(collectConfig()) !== savedSnapshot;
  unsavedNote.style.display = dirty ? '' : 'none';
}

// ─── Public API ───

/** Wire the panel once, on page load. */
export function initBrandingPanel(): void {
  initCounters();

  colorPicker.addEventListener('input', () => {
    colorHex.value = colorPicker.value.toUpperCase();
    updatePreview();
  });

  textColorPicker.addEventListener('input', () => {
    textColorHex.value = textColorPicker.value.toUpperCase();
    updatePreview();
  });

  const textFields: Array<HTMLInputElement | HTMLTextAreaElement> = [
    brandTitle, brandDesc, colorHex, textColorHex, logoUrl, backgroundUrl, promoBackgroundUrl,
    ctaTitle, ctaSubtitle, ctaButton, ctaUrl,
  ];
  textFields.forEach((input) => input.addEventListener('input', updatePreview));

  [showInPlayer, showSidebar, showInPopup]
    .forEach((input) => input.addEventListener('change', updatePreview));

  addLinkBtn.addEventListener('click', () => {
    addLinkRow();
    updatePreview();
  });

  resetBtn.addEventListener('click', () => {
    if (!savedConfig) return;
    setError(null);
    populateForm(savedConfig);
  });

  saveBtn.addEventListener('click', async () => {
    const token = getStoredToken();
    if (!token) return;

    setError(null);
    window.clearTimeout(savedLabelTimer);
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';

    try {
      savedConfig = await saveBranding(token, collectConfig());
      populateForm(savedConfig);
      badge.textContent = isConfigured(savedConfig) ? 'Live' : 'Not set up';
      badge.className = isConfigured(savedConfig)
        ? 'fc-badge fc-badge--ok'
        : 'fc-badge';
      // Confirmed on the button that did it, the way the passkey's Copy does.
      // It is where the reader is already looking, and it cannot be missed by
      // someone who glanced away.
      saveBtn.textContent = 'Saved';
      // Kept so a second save does not get relabelled by the first one's timer.
      window.clearTimeout(savedLabelTimer);
      savedLabelTimer = window.setTimeout(() => { saveBtn.textContent = 'Save branding'; }, 2000);
    } catch (err: unknown) {
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : 'Could not save your branding.';
      setError(message);
      saveBtn.textContent = 'Save branding';
    } finally {
      saveBtn.disabled = false;
    }
  });
}

/**
 * Load and render the panel for the signed-in creator.
 * Branding is a paid feature, so non-subscribers see the locked notice.
 */
export async function renderBrandingPanel(
  hasActiveSubscription: boolean,
  creatorChannelName: string,
): Promise<void> {
  channelName = creatorChannelName;
  setError(null);

  if (!hasActiveSubscription) {
    lockedNotice.style.display = '';
    editor.style.display = 'none';
    badge.textContent = 'Locked';
    badge.className = 'fc-badge';
    return;
  }

  lockedNotice.style.display = 'none';
  editor.style.display = '';

  const token = getStoredToken();
  if (!token) return;

  try {
    savedConfig = await getBranding(token);
  } catch {
    // Nothing stored yet, or the request failed — start from an empty form.
    savedConfig = {};
  }

  channelName = savedConfig.channel_name || creatorChannelName;
  populateForm(savedConfig);

  const configured = isConfigured(savedConfig);
  badge.textContent = configured ? 'Live' : 'Not set up';
  badge.className = configured
    ? 'fc-badge fc-badge--ok'
    : 'fc-badge';
}
